import api from '@dashboard/services/axiosApi'

type TimingKey = 'firstRenderMs' | 'firstTextMs'

/** Entries beyond this are dropped oldest first, so a long session does not grow these maps without bound. */
const MAX_ENTRIES = 100

const submittedAt = new Map<string, number>()
/** First measured value per `${turnId}:${key}`; kept until its post succeeds so a retry sends the same value. */
const measured = new Map<string, number>()
const reported = new Set<string>()

function trim(entries: { size: number; keys(): IterableIterator<string>; delete(key: string): boolean }): void {
  for (const key of entries.keys()) {
    if (entries.size <= MAX_ENTRIES) {
      return
    }
    entries.delete(key)
  }
}

export function markTurnSubmitted(clientTurnId: string): void {
  submittedAt.set(clientTurnId, performance.now())
  trim(submittedAt)
}

/** Reports how long after submit the browser first showed server progress or answer text. */
export function reportTurnTiming(teamPid: string, turnId: string, clientTurnId: string, key: TimingKey): void {
  const startedAt = submittedAt.get(clientTurnId)
  const reportKey = `${turnId}:${key}`
  if (startedAt === undefined || reported.has(reportKey)) {
    return
  }
  const elapsed = measured.get(reportKey) ?? Math.round(performance.now() - startedAt)
  measured.set(reportKey, elapsed)
  reported.add(reportKey)
  trim(measured)
  trim(reported)
  // A failed post frees the key, so the next rendered event retries with the first measured value.
  void postTiming(teamPid, turnId, { [key]: elapsed })
    .then(() => measured.delete(reportKey))
    .catch(() => reported.delete(reportKey))
}

async function postTiming(teamPid: string, turnId: string, body: Partial<Record<TimingKey, number>>): Promise<void> {
  await api.post(`/api/${teamPid}/righty/turns/${turnId}/client-timing`, body)
}
